import { useState } from "react";
import { toast } from "sonner";
import { Mail, Send } from "lucide-react";
import { sendContact } from "../lib/api";
import NewsletterForm from "../components/NewsletterForm";
import SEO from "../components/SEO";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", organization: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Fill in name, email, subject, and message.");
      return;
    }
    setLoading(true);
    try {
      await sendContact(form);
      toast.success("Message received. We'll be in touch.");
      setForm({ name: "", email: "", organization: "", subject: "", message: "" });
    } catch {
      toast.error("Couldn't send. Try again in a moment.");
    } finally { setLoading(false); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div data-testid="contact-page">
      <SEO
        title="Contact · Press, Booking & Pitches"
        description="Press inquiries, interview requests, brand partnerships, and guest pitches — drop a line and we'll get back within 48 hours."
      />
      <section className="border-b-2 border-[var(--ink)]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-14 md:py-20">
          <div className="text-kicker">Get in Touch</div>
          <h1 className="text-headline text-5xl md:text-7xl lg:text-8xl mt-2">
            Pitch us. <span className="text-[var(--rust)]">Book us.</span> Argue with us.
          </h1>
          <p className="text-dek text-xl md:text-2xl mt-4 max-w-2xl">
            Press inquiries, interview requests, brand partnerships, guest pitches — drop a line and we'll get back within 48 hours.
          </p>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 grid md:grid-cols-3 gap-12">
          <div className="md:col-span-2">
            <div className="sticker-card p-7 md:p-10">
              <h2 className="text-headline text-3xl md:text-4xl mb-6">The Form</h2>
              <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-kicker block mb-1">Name</label>
                    <input className="field" required value={form.name} onChange={set("name")} data-testid="contact-name-input" />
                  </div>
                  <div>
                    <label className="text-kicker block mb-1">Email</label>
                    <input className="field" type="email" required value={form.email} onChange={set("email")} data-testid="contact-email-input" />
                  </div>
                </div>
                <div>
                  <label className="text-kicker block mb-1">Organization (optional)</label>
                  <input className="field" value={form.organization} onChange={set("organization")} data-testid="contact-organization-input" />
                </div>
                <div>
                  <label className="text-kicker block mb-1">Subject</label>
                  <input className="field" required value={form.subject} onChange={set("subject")} data-testid="contact-subject-input" />
                </div>
                <div>
                  <label className="text-kicker block mb-1">Message</label>
                  <textarea className="field min-h-[140px] resize-y" required
                            value={form.message} onChange={set("message")}
                            data-testid="contact-message-input" />
                </div>
                <button className="btn-rust" type="submit" disabled={loading} data-testid="contact-submit-button">
                  {loading ? "Sending…" : "Send Message"} <Send size={16} />
                </button>
              </form>
            </div>
          </div>

          <aside className="space-y-8">
            <div>
              <div className="text-kicker">Direct</div>
              <h3 className="text-headline text-2xl mt-1 mb-3">Press Email</h3>
              <a href="mailto:press@unfilteredandonball.com" className="inline-flex items-center gap-2 font-serif-ed italic text-lg underline underline-offset-4 hover:text-[var(--rust)]">
                <Mail size={16} /> press@unfilteredandonball.com
              </a>
            </div>
            <div className="rule-thick pt-8">
              <div className="text-kicker">Faster Response</div>
              <h3 className="text-headline text-2xl mt-1 mb-3">DM Us</h3>
              <p className="text-dek text-base mb-3">Any of our socials work — but IG and Twitter/X are the fastest.</p>
              <div className="flex flex-wrap gap-2">
                <a href="https://instagram.com/Unfilteredandonball" target="_blank" rel="noreferrer" className="chip">Instagram</a>
                <a href="https://tiktok.com/@Unfilteredandonball" target="_blank" rel="noreferrer" className="chip">TikTok</a>
                <a href="https://twitch.tv/Unfilteredandonball" target="_blank" rel="noreferrer" className="chip">Twitch</a>
              </div>
            </div>
            <div className="rule-thick pt-8">
              <div className="text-kicker">Newsletter</div>
              <h3 className="text-headline text-2xl mt-1 mb-3">Join the Huddle</h3>
              <NewsletterForm variant="stacked" source="contact_page" />
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
